import { ApiProblem, CLIENT_CODES, toProblemBody } from './problem';
import type { LoginResponse } from './types';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientConfig {
  /** e.g. "/api/v1" (proxied to the API in development). */
  baseUrl: string;
  fetch?: typeof fetch;
  /** In-memory access token (never persisted). */
  getAccessToken: () => string | null;
  /** Sent as Accept-Language so problem titles/details come back localized. */
  getLocale: () => string;
  /** Active organization for X-Organization-Id. */
  getOrganizationId: () => string | null;
  /** New tokens + user from POST /auth/refresh. */
  onSessionRefreshed: (response: LoginResponse) => void;
  /** Refresh failed after a 401 → sign the user out (redirect to /login). */
  onAuthLost: () => void;
  /** 403 STEP_UP_REQUIRED → open the re-auth dialog; resolve true to retry once. */
  onStepUpRequired?: () => Promise<boolean>;
  /** 403 MFA_ENROLLMENT_REQUIRED → surface the enrolment banner. */
  onMfaEnrollmentRequired?: () => void;
  /** Value of the non-httpOnly tb_csrf cookie (double-submit CSRF for /auth/refresh). */
  readCsrfToken: () => string | null;
  sleep?: (ms: number) => Promise<void>;
}

export interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** false → no bearer token and no refresh-on-401 (public auth endpoints). */
  auth?: boolean;
  /** Override the X-Organization-Id header; null omits it. */
  organizationId?: string | null;
  /** false → do not open the step-up dialog on STEP_UP_REQUIRED. */
  stepUp?: boolean;
  /** 'blob' returns the raw body (CSV exports, file downloads) instead of parsed JSON. */
  responseType?: 'json' | 'blob';
}

export interface ApiClient {
  request: <T>(path: string, options?: RequestOptions) => Promise<T>;
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) => Promise<T>;
  /** GET a binary/text body (e.g. ?format=csv) through the same auth, refresh and step-up flow. */
  blob: (path: string, options?: Omit<RequestOptions, 'method' | 'body' | 'responseType'>) => Promise<Blob>;
  /** Single-flight POST /auth/refresh. Resolves false when the session cannot be restored. */
  refreshSession: () => Promise<boolean>;
}

interface Attempt {
  refreshed: boolean;
  steppedUp: boolean;
}

export const REFRESH_PATH = '/auth/refresh';

/**
 * 401s that report a failed credential check (wrong password/code), not an expired session.
 * Refreshing would not help, so they are surfaced to the form directly.
 */
export const CREDENTIAL_401_CODES: ReadonlySet<string> = new Set([
  'AUTH_INVALID_CREDENTIALS',
  'AUTH_MFA_INVALID',
  'AUTH_MFA_REQUIRED',
  'AUTH_EMAIL_NOT_VERIFIED',
]);
export const REFRESH_RACE_RETRY_MS = 300;
const ACCEPT = 'application/json, application/problem+json';

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const fetchImpl: typeof fetch = config.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const sleep = config.sleep ?? defaultSleep;
  let inflightRefresh: Promise<boolean> | null = null;

  const url = (path: string) => `${config.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;

  async function readBody(response: Response): Promise<unknown> {
    const text = await response.text();
    if (!text) return undefined;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new ApiProblem({
        type: 'about:blank',
        title: response.statusText,
        status: response.status,
        code: CLIENT_CODES.invalidResponse,
      });
    }
  }

  async function toProblem(response: Response): Promise<ApiProblem> {
    let payload: unknown = null;
    try {
      payload = await readBody(response);
    } catch {
      payload = null;
    }
    return new ApiProblem(toProblemBody(payload, response.status, response.statusText));
  }

  async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: ACCEPT,
      'Accept-Language': config.getLocale(),
      ...options.headers,
    };
    const multipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
    // Multipart: the browser sets the boundary itself.
    if (options.body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    const organizationId = options.organizationId !== undefined ? options.organizationId : config.getOrganizationId();
    if (organizationId) headers['X-Organization-Id'] = organizationId;

    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
    };
    if (options.body !== undefined) init.body = multipart ? (options.body as FormData) : JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;

    try {
      return await fetchImpl(url(path), init);
    } catch (error) {
      if (isAbort(error)) throw error;
      throw new ApiProblem({ type: 'about:blank', title: '', status: 0, code: CLIENT_CODES.network });
    }
  }

  async function doRefresh(): Promise<boolean> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const csrf = config.readCsrfToken();
      // No CSRF cookie means no refresh cookie was ever issued to this browser.
      if (!csrf) return false;
      let response: Response;
      try {
        response = await fetchImpl(url(REFRESH_PATH), {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: ACCEPT, 'Accept-Language': config.getLocale(), 'X-CSRF-Token': csrf },
        });
      } catch {
        return false;
      }
      if (response.ok) {
        const body = (await readBody(response)) as LoginResponse | undefined;
        if (!body?.access_token) return false;
        config.onSessionRefreshed(body);
        return true;
      }
      if (response.status === 409 && attempt === 0) {
        const problem = await toProblem(response);
        // Another tab rotated the token within the grace window: wait for its cookie, retry once.
        if (problem.code === 'REFRESH_RACE') {
          await sleep(REFRESH_RACE_RETRY_MS);
          continue;
        }
      }
      return false;
    }
    return false;
  }

  function refreshSession(): Promise<boolean> {
    inflightRefresh ??= doRefresh().finally(() => {
      inflightRefresh = null;
    });
    return inflightRefresh;
  }

  async function run<T>(path: string, options: RequestOptions, attempt: Attempt): Promise<T> {
    const token = options.auth === false ? null : config.getAccessToken();
    const response = await send(path, options, token);
    if (response.ok) return (options.responseType === 'blob' ? await response.blob() : await readBody(response)) as T;

    const problem = await toProblem(response);

    if (response.status === 401 && token && !attempt.refreshed && !CREDENTIAL_401_CODES.has(problem.code)) {
      // A parallel request may already have refreshed: replay with the newer token first.
      const current = config.getAccessToken();
      const refreshed = current !== null && current !== token ? true : await refreshSession();
      if (refreshed) return run<T>(path, options, { ...attempt, refreshed: true });
      config.onAuthLost();
      throw problem;
    }

    if (
      response.status === 403 &&
      problem.code === 'STEP_UP_REQUIRED' &&
      options.stepUp !== false &&
      !attempt.steppedUp &&
      config.onStepUpRequired
    ) {
      const confirmed = await config.onStepUpRequired();
      if (confirmed) return run<T>(path, options, { ...attempt, steppedUp: true });
    }

    if (response.status === 403 && problem.code === 'MFA_ENROLLMENT_REQUIRED') {
      config.onMfaEnrollmentRequired?.();
    }

    throw problem;
  }

  const request = <T>(path: string, options: RequestOptions = {}) =>
    run<T>(path, options, { refreshed: false, steppedUp: false });

  type NoBody = Omit<RequestOptions, 'method' | 'body'>;
  return {
    request,
    get: <T>(path: string, options?: NoBody) => request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, body?: unknown, options?: NoBody) => request<T>(path, { ...options, method: 'POST', body }),
    put: <T>(path: string, body?: unknown, options?: NoBody) => request<T>(path, { ...options, method: 'PUT', body }),
    patch: <T>(path: string, body?: unknown, options?: NoBody) => request<T>(path, { ...options, method: 'PATCH', body }),
    delete: <T>(path: string, options?: NoBody) => request<T>(path, { ...options, method: 'DELETE' }),
    blob: (path: string, options?: Omit<NoBody, 'responseType'>) => request<Blob>(path, { ...options, method: 'GET', responseType: 'blob' }),
    refreshSession,
  };
}
